import React, { useEffect, useMemo } from 'react';
import styled from 'styled-components';
import UniversalIconBtn from '../../shared/component/headless/button/UniversalIconBtn';
import { BlackFullWidthBtn } from '../../shared/component/headless/button/BtnBody';
import { useGroupMember } from '../../shared/hooks/useApi/useGroupMember';
import { useGroupMemberStore } from '../../shared/store/groupMemberStore';
import { useGroupMemberLinkStore } from '../../shared/store/groupMemberLinkStore';
import { useModalStore } from '../../shared/store/portal/modalStore';
import { useLoadingStore } from '../../shared/store/loadingStore';
import { getModalMessage } from '../../shared/constants/modalMessages';
import { getBtnName } from '../../shared/constants/btnNames';
import { SocialMedia } from '../../shared/dto/EntityRepository';
import deleteIcon from '../../asset/icon/delete.png';

interface GroupMemberLinkConfirmModalProps {
  setIsUserInput: (isUserInput: boolean) => void;
}

const formatSns = (sns: SocialMedia): string => {
  const values = [
    sns.instagram && `Instagram: @${sns.instagram}`,
    sns.twitter && `Twitter: ${sns.twitter}`,
    sns.facebook && `Facebook: ${sns.facebook}`,
    sns.youtube && `YouTube: ${sns.youtube}`,
    sns.url1 && `URL: ${sns.url1}`,
  ].filter(Boolean);
  return values.join('\n');
};

const GroupMemberLinkConfirmModal: React.FC<GroupMemberLinkConfirmModalProps> = ({
  setIsUserInput,
}) => {
  const { standardModal, clearModal } = useModalStore();
  const member = useGroupMemberStore((state) =>
    state.groupMemberList.find((item) => item.id === standardModal?.value),
  );
  const { overwriteData, clearOverwriteData } = useGroupMemberLinkStore();
  const { linkGroupMember } = useGroupMember();
  const { isLoading } = useLoadingStore();

  useEffect(() => {
    setIsUserInput(true);
    return () => clearOverwriteData();
  }, [clearOverwriteData, setIsUserInput]);

  const overwriteRows = useMemo(() => {
    if (!member || !overwriteData) return [];
    return [
      { label: 'Name', before: member.name, after: overwriteData.name },
      { label: 'Name (EN)', before: member.nameEn, after: overwriteData.nameEn },
      { label: 'Born', before: member.year ? String(member.year) : '', after: overwriteData.year ? String(overwriteData.year) : '' },
      { label: 'Country', before: member.country === 'NONE' ? '' : member.country, after: overwriteData.country === 'NONE' ? '' : overwriteData.country },
      { label: 'Email', before: member.email, after: overwriteData.email },
      { label: 'Contact', before: member.contact, after: overwriteData.contact },
      { label: 'Description', before: member.description, after: overwriteData.description },
      { label: 'Description (EN)', before: member.descriptionEn, after: overwriteData.descriptionEn },
      { label: 'SNS', before: formatSns(member.sns), after: formatSns(overwriteData.sns) },
    ];
  }, [member, overwriteData]);

  if (!member || !overwriteData || !standardModal) return null;

  const handleConfirm = async () => {
    const connected = await linkGroupMember({
      id: member.id,
      linkToken: overwriteData.linkToken,
    });
    if (!connected) return;
    clearModal();
  };

  const handleClose = () => {
    clearModal();
  };

  return (
    <ConfirmComp>
      <HeaderRow>
        <Title>{getModalMessage('GROUP_MEMBER_LINK_CONFIRM_TITLE')}</Title>
        <UniversalIconBtn
          size="small"
          border={false}
          isTransparent
          hoverOpacity
          backgroundImg={deleteIcon}
          onClick={handleClose}
        />
      </HeaderRow>
      <Notice>{getModalMessage('GROUP_MEMBER_LINK_CONFIRM_NOTICE')}</Notice>
      <OverwriteList>
        {overwriteRows.map((row) => (
          <OverwriteRow key={row.label}>
            <RowLabel>{row.label}</RowLabel>
            <ValueGrid>
              <BeforeValue>
                {row.before || '-'}
              </BeforeValue>
              <Arrow>→</Arrow>
              <AfterValue $isCleared={!!row.before && !row.after}>
                {row.after
                  || (row.before ? getModalMessage('GROUP_MEMBER_LINK_EMPTY') : '-')}
              </AfterValue>
            </ValueGrid>
          </OverwriteRow>
        ))}
      </OverwriteList>
      <SubmitButton onClick={handleConfirm}>
        {getBtnName('OK')}
      </SubmitButton>
    </ConfirmComp>
  );
};

export default GroupMemberLinkConfirmModal;

const ConfirmComp = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  max-height: min(78vh, 720px);
  padding: 17px 14px;
`;

const HeaderRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  margin-bottom: 3vh;
`;

const Title = styled.h2`
  min-width: 0;
  color: ${({ theme }) => theme.colors.color_Gray_02};
  ${({ theme }) => theme.typography.Body_02_1};
`;

const Notice = styled.p`
  color: ${({ theme }) => theme.colors.color_Gray_03};
  ${({ theme }) => theme.typography.Body_04};
`;

const OverwriteList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 14px;
  overflow-y: auto;
  padding-right: 4px;
`;

const OverwriteRow = styled.div`
  display: flex;
  flex-direction: column;
  gap: 5px;
`;

const RowLabel = styled.div`
  color: ${({ theme }) => theme.colors.color_Gray_04};
  ${({ theme }) => theme.typography.Body_04};
`;

const ValueGrid = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto minmax(0, 1fr);
  gap: 8px;
  align-items: start;
`;

const OverwriteValue = styled.div`
  min-width: 0;
  white-space: pre-wrap;
  overflow-wrap: anywhere;
  ${({ theme }) => theme.typography.Body_04};
`;

const BeforeValue = styled(OverwriteValue)`
  color: ${({ theme }) => theme.colors.color_Gray_05};
`;

const AfterValue = styled(OverwriteValue)<{ $isCleared: boolean }>`
  color: ${({ theme, $isCleared }) =>
    $isCleared ? theme.colors.color_alert_red : theme.colors.color_Gray_02};
`;

const Arrow = styled.div`
  color: ${({ theme }) => theme.colors.color_Gray_05};
  ${({ theme }) => theme.typography.Body_04};
`;

const SubmitButton = styled(BlackFullWidthBtn)`
  margin: 10px 0px 20px 0;
`;
